import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "Prices", id: "pricing" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

const Logo = () => (
  <button
    data-testid="nav-logo"
    onClick={() => scrollToId("home")}
    className="flex items-center gap-3 group"
  >
    <span className="font-display text-2xl font-extrabold leading-none tracking-tighter text-ink">
      MM
    </span>
    <span className="hidden sm:flex flex-col leading-none">
      <span className="font-display text-sm font-bold tracking-[0.2em] text-ink">DIGITAL</span>
      <span className="font-mono text-[9px] tracking-[0.45em] text-gold">AGENCY</span>
    </span>
  </button>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-white/80 backdrop-blur-xl border-b border-ink/10" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Logo />

          <div className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="nav-link font-mono text-[12px] uppercase tracking-[0.15em] text-ink/70 hover:text-ink transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-contact-btn"
              onClick={() => go("contact")}
              className="hidden sm:inline-flex items-center gap-2 bg-gold text-ink font-mono text-[12px] uppercase tracking-[0.12em] px-5 py-2.5 rounded-full hover:bg-cream transition-colors duration-300"
            >
              Get a Quote <ArrowUpRight weight="bold" size={14} />
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden text-ink p-2"
              aria-label="Menu"
            >
              {open ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-white lg:hidden flex flex-col justify-center px-8"
            data-testid="mobile-menu"
          >
            {LINKS.concat({ label: "Contact", id: "contact" }).map((l, i) => (
              <motion.button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => go(l.id)}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 * i }}
                className="text-left font-display text-4xl font-bold text-ink py-3 border-b border-ink/10"
              >
                <span className="font-mono text-xs text-gold mr-4">0{i + 1}</span>
                {l.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
